import { motion } from 'framer-motion';
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLinkIcon,
  BrandMark,
  CardShell,
  LoadingScreen,
  SectionHeader,
  SectionTag,
  TealButton,
  staggerContainer,
  staggerItem,
} from '../components/common/BrandElements';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { getAbout, getCompanies, getNews, getSliders } from '../lib/api';

const fallbackSlides = [
  {
    id: 'fallback-brand',
    title_ar: 'مجموعة رعد القابضة',
    title_en: 'Raad Holding Group',
    description_ar: 'شركات متنوعة، رؤية واحدة، وخدمات متكاملة تنطلق من دمشق إلى قطاعات متعددة.',
    description_en: 'Diverse companies, one vision, and integrated services growing from Damascus across multiple sectors.',
    image_url: '/assets/raad-identity-hero.webp',
  }
];

const HomePage = () => {
  const { t, isRTL } = useLanguage();
  const [sliders, setSliders] = useState([]);
  const [about, setAbout] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [news, setNews] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [slidersRes, aboutRes, companiesRes, newsRes] = await Promise.all([
          getSliders(),
          getAbout(),
          getCompanies(),
          getNews()
        ]);
        setSliders(
          slidersRes.data
            .filter(s => s.is_active)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        );
        setAbout(aboutRes.data);
        setCompanies(companiesRes.data.filter(c => c.is_active));
        setNews(newsRes.data.filter(n => n.is_active).slice(0, 3));
      } catch (error) {
        console.error('Error loading home data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const heroSlides = sliders.length > 0 ? sliders : fallbackSlides;
  const activeIndex = ((currentSlide % heroSlides.length) + heroSlides.length) % heroSlides.length;
  const activeSlide = heroSlides[activeIndex];
  const activeImage = activeSlide.image_url || '/assets/raad-identity-hero.png';

  useEffect(() => {
    if (heroSlides.length > 1) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      }, 6500);
      return () => clearInterval(timer);
    }
  }, [heroSlides.length]);

  const goToSlide = (index) => setCurrentSlide(((index % heroSlides.length) + heroSlides.length) % heroSlides.length);
  const nextSlide = () => goToSlide(activeIndex + 1);
  const prevSlide = () => goToSlide(activeIndex - 1);

  if (loading) {
    return <LoadingScreen />;
  }


  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden bg-raad-porcelain pt-36 pb-20 lg:pt-44 lg:pb-24">
        <div className="brand-grid pointer-events-none absolute inset-0 opacity-70" />
        <div className="pointer-events-none absolute -top-24 end-[-8rem] h-96 w-96 rounded-full bg-raad-teal/15 blur-3xl" />
        <div className="pointer-events-none absolute start-0 bottom-0 h-8 w-[26rem] max-w-[70vw] bg-raad-teal/20 brand-slash" />

        <div className="container relative z-10 mx-auto px-6">
          <div className="grid items-center gap-12 lg:grid-cols-[1.02fr_0.98fr]">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="max-w-3xl"
            >
              <motion.div
                key={activeSlide.id || activeIndex}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
              >
                <h1 className="text-5xl font-black leading-[1.04] tracking-tight text-raad-charcoal sm:text-6xl lg:text-7xl text-balance">
                  {t(activeSlide.title_ar, activeSlide.title_en)}
                </h1>
                <p className="mt-6 max-w-2xl text-lg leading-9 text-raad-graphite/75 sm:text-xl">
                  {t(activeSlide.description_ar, activeSlide.description_en)}
                </p>
              </motion.div>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <TealButton to="/companies" isRTL={isRTL} size="lg" className="px-7">
                  {t('استكشف شركاتنا', 'Explore Our Companies')}
                </TealButton>
                <TealButton to="/contact" variant="outline" size="lg" className="px-7">
                  {t('تواصل معنا', 'Contact Us')}
                </TealButton>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.1 }}
              className="relative"
            >
              <div className="absolute -inset-5 rounded-[2.5rem] border border-raad-teal/20" />
              <div className="relative overflow-hidden rounded-[2.25rem] border border-white bg-white shadow-soft">
                <div className="absolute end-0 top-0 z-10 h-8 w-64 bg-raad-teal/20 brand-slash-end" />
                <motion.img
                  key={activeImage}
                  initial={{ opacity: 0, scale: 1.035 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.45 }}
                  src={activeImage}
                  alt={t(activeSlide.title_ar, activeSlide.title_en)}
                  className="h-[540px] w-full object-cover object-bottom"
                />
              </div>

              {heroSlides.length > 1 && (
                <div className="relative z-20 mt-6 flex items-center justify-between gap-4">
                  <div className="flex gap-2">
                    {heroSlides.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => goToSlide(index)}
                        className={`h-2 rounded-full transition-all ${index === activeIndex ? 'w-9 bg-raad-teal' : 'w-2 bg-raad-teal/25 hover:bg-raad-teal/50'}`}
                        aria-label={`Slide ${index + 1}`}
                        aria-current={index === activeIndex}
                      />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={prevSlide} className="flex h-11 w-11 items-center justify-center rounded-full border border-raad-teal/20 bg-white text-raad-charcoal hover:bg-raad-teal hover:text-white" aria-label="Previous slide">
                      {isRTL ? <ChevronRight /> : <ChevronLeft />}
                    </button>
                    <button type="button" onClick={nextSlide} className="flex h-11 w-11 items-center justify-center rounded-full border border-raad-teal/20 bg-white text-raad-charcoal hover:bg-raad-teal hover:text-white" aria-label="Next slide">
                      {isRTL ? <ChevronLeft /> : <ChevronRight />}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {about && (
        <section className="relative overflow-hidden bg-white py-24">
          <div className="absolute end-0 top-0 h-8 w-96 max-w-[80vw] bg-raad-teal/20 brand-slash-end" />
          <div className="container mx-auto px-6">
            <div className="grid items-center gap-14 lg:grid-cols-2">
              <motion.div
                initial={{ opacity: 0, x: isRTL ? 40 : -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <SectionTag>{t('من نحن', 'About Us')}</SectionTag>
                <h2 className="mt-5 text-4xl font-black leading-tight text-raad-charcoal lg:text-5xl text-balance">
                  {t(about.title_ar, about.title_en)}
                </h2>
                <p className="mt-6 text-lg leading-9 text-raad-graphite/70 whitespace-pre-wrap">
                  {t(about.content_ar, about.content_en)}
                </p>
                <TealButton to="/companies" isRTL={isRTL} className="mt-8 px-7">
                  {t('تعرف على شركاتنا', 'Learn About Our Companies')}
                </TealButton>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: isRTL ? -40 : 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="absolute -inset-4 rounded-[2.25rem] border border-raad-teal/20" />
                <div className="relative overflow-hidden rounded-[2rem] bg-raad-charcoal shadow-soft">
                  <img
                    src={about.image_url || '/assets/raad-identity-hero.png'}
                    alt={t(about.title_ar, about.title_en)}
                    className="h-[430px] w-full object-cover opacity-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-raad-charcoal/70 via-transparent to-transparent" />
                  <div className="absolute bottom-6 start-6 rounded-2xl bg-white p-5 shadow-soft">
                    <p className="text-4xl font-black text-raad-teal">{companies.length}+</p>
                    <p className="text-sm font-bold text-raad-charcoal">{t('شركات تابعة', 'Companies')}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      <section className="relative overflow-hidden bg-raad-porcelain py-24">
        <div className="brand-grid absolute inset-0 opacity-45" />
        <div className="container relative z-10 mx-auto px-6">
          <SectionHeader
            kicker={t('شركاتنا', 'Our Companies')}
            title={t('مجموعة متنوعة، رؤية واحدة', 'Diverse companies, one vision')}
            description={t('كل شركة تعمل ضمن إطار واضح: حماية، ابتكار، ونمو مستدام.', 'Each company works within a clear frame: protection, innovation, and sustainable growth.')}
            className="mb-14"
          />

          <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {companies.slice(0, 8).map((company) => (
              <motion.div key={company.id} variants={staggerItem}>
                <Link to={`/companies/${company.slug}`} data-testid={`company-card-${company.slug}`}>
                  <CardShell className="h-full p-6">
                    <div className="relative z-10">
                      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-raad-mist">
                        {company.logo_url ? (
                          <img src={company.logo_url} alt={t(company.name_ar, company.name_en)} className="h-12 w-12 object-contain" />
                        ) : (
                          <span className="text-2xl font-black text-raad-teal">{(t(company.name_ar, company.name_en) || 'R')[0]}</span>
                        )}
                      </div>
                      <h3 className="text-xl font-black text-raad-charcoal group-hover:text-raad-teal">
                        {t(company.name_ar, company.name_en)}
                      </h3>
                      <p className="mt-3 min-h-[3.75rem] text-sm leading-6 text-raad-graphite/65 line-clamp-3">
                        {t(company.description_ar, company.description_en)}
                      </p>
                      <div className="mt-6 inline-flex items-center gap-2 text-sm font-black text-raad-teal">
                        {t('المزيد', 'Learn More')}
                        <ArrowUpRight className="h-4 w-4" />
                      </div>
                    </div>
                  </CardShell>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <div className="mt-12 text-center">
            <TealButton to="/companies" variant="outline" isRTL={isRTL} size="lg" className="px-7">
              {t('عرض جميع الشركات', 'View All Companies')}
            </TealButton>
          </div>
        </div>
      </section>

      {news.length > 0 && (
        <section className="bg-white py-24">
          <div className="container mx-auto px-6">
            <div className="mb-12 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
              <SectionHeader
                align="start"
                kicker={t('آخر الأخبار', 'Latest News')}
                title={t('أحدث الأخبار والتحديثات', 'News & Updates')}
                description={t('تابع نشاطات المجموعة وشركاتها.', 'Follow the group and its companies.')}
              />
              <Button asChild variant="ghost" className="self-start rounded-full text-raad-teal hover:bg-raad-teal/10 hover:text-raad-charcoal">
                <Link to="/news">
                  {t('عرض الكل', 'View All')}
                  <ArrowLinkIcon isRTL={isRTL} className="ms-2" />
                </Link>
              </Button>
            </div>

            <div className="grid gap-7 md:grid-cols-3">
              {news.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                >
                  <Link to={`/news/${item.slug}`} className="group block">
                    <CardShell>
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={item.image_url || '/assets/raad-identity-hero.png'}
                          alt={t(item.title_ar, item.title_en)}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-6">
                        <p className="text-xs font-black uppercase tracking-[0.22em] text-raad-teal">
                          {new Date(item.published_at).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
                        </p>
                        <h3 className="mt-3 text-xl font-black text-raad-charcoal group-hover:text-raad-teal line-clamp-2">
                          {t(item.title_ar, item.title_en)}
                        </h3>
                      </div>
                    </CardShell>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="relative overflow-hidden bg-raad-charcoal py-24 text-white">
        <div className="brand-grid absolute inset-0 opacity-20" />
        <div className="absolute start-0 bottom-0 h-8 w-[30rem] max-w-[75vw] bg-raad-teal/70 brand-slash" />
        <div className="container relative z-10 mx-auto px-6 text-center">
          <BrandMark className="mx-auto mb-6 h-16 w-16 brightness-0 invert" />
          <h2 className="text-4xl font-black leading-tight lg:text-5xl">
            {t('هل أنت مستعد للانضمام إلى رحلتنا؟', 'Ready to join our journey?')}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-white/70">
            {t('نحن نبحث دائماً عن مواهب وشراكات تصنع أثراً طويل الأمد.', 'We are always looking for talent and partnerships that create lasting impact.')}
          </p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <Button asChild size="lg" className="rounded-full bg-white px-8 text-raad-charcoal hover:bg-raad-teal hover:text-white">
              <Link to="/careers">{t('تصفح الوظائف', 'Browse Jobs')}</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-white/40 px-8 text-white hover:bg-white/10">
              <Link to="/contact">{t('تواصل معنا', 'Contact Us')}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
